import { Flex } from 'antd';
import type { KeyboardEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '../Ant';
import type { QuickActionGroup, QuickActionItem } from './quickActionGroups';

export const MENU_ITEM_SELECTOR = '[role="menuitem"]';

export const resolveNextMenuItemIndex = (key: string, currentIndex: number, itemCount: number) => {
  switch (key) {
    case 'ArrowDown':
      return (currentIndex + 1) % itemCount;
    case 'ArrowUp':
      return currentIndex <= 0 ? itemCount - 1 : currentIndex - 1;
    case 'Home':
      return 0;
    case 'End':
      return itemCount - 1;
    default:
      return null;
  }
};

const moveFocusWithinMenu = (event: KeyboardEvent<HTMLElement>) => {
  const items = [...event.currentTarget.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR)];
  if (items.length === 0) return;

  const currentIndex = items.indexOf(document.activeElement as HTMLElement);
  const nextIndex = resolveNextMenuItemIndex(event.key, currentIndex, items.length);
  if (nextIndex === null) return;

  event.preventDefault();
  items[nextIndex].focus();
};

interface QuickActionMenuProps {
  groups: QuickActionGroup[];
  onSelect: (action: QuickActionItem) => void;
  ref?: Ref<HTMLDivElement>;
}

const QuickActionMenu = ({ groups, onSelect, ref }: Readonly<QuickActionMenuProps>) => {
  const { t } = useTranslation();

  return (
    <Flex ref={ref} vertical gap={10} role="menu" onKeyDown={moveFocusWithinMenu}>
      {groups.map((group) => (
        <fieldset
          key={group.key}
          className="quick-action-group"
          data-testid={`quick-action-group-${group.key}`}
        >
          <Text
            as="legend"
            $fontsize="11px"
            $lineheight="14px"
            $weight={500}
            $color="description"
            $mb={5}
          >
            {t(group.headingKey)}
          </Text>
          <Flex vertical>
            {group.actions.map((action) => {
              const ActionIcon = action.icon;
              return (
                <Button
                  key={action.key}
                  type="text"
                  block
                  role="menuitem"
                  className="quick-action-item"
                  icon={<ActionIcon />}
                  onClick={() => onSelect(action)}
                  data-testid={`quick-action-${action.key}`}
                >
                  <Text $fontsize="13px" $weight={500} $color="title" $width="auto">
                    {t(action.translationKey)}
                  </Text>
                </Button>
              );
            })}
          </Flex>
        </fieldset>
      ))}
    </Flex>
  );
};

export default QuickActionMenu;
